import { useState, useEffect } from 'react';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Trash2 } from 'lucide-react';

export const DeleteLLCDialog = ({ open, onOpenChange, llcName, onDelete, dataCount }) => {
  const [step, setStep] = useState(1);
  const [typedName, setTypedName] = useState('');
  const hasData = dataCount && dataCount > 0;

  useEffect(() => {
    if (!open) { setStep(1); setTypedName(''); }
  }, [open]);

  const nameMatch = typedName.trim().toLowerCase() === (llcName || '').trim().toLowerCase();

  if (!hasData) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete LLC?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{llcName}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button onClick={onDelete} variant="destructive" data-testid="confirm-delete-btn">Delete</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        {step === 1 && (
          <>
            <AlertDialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-rose-600" />
                </div>
                <AlertDialogTitle className="text-rose-700">Warning: LLC Has Data</AlertDialogTitle>
              </div>
              <AlertDialogDescription className="text-left space-y-3">
                <p><strong>{llcName}</strong> has <strong>{dataCount.toLocaleString()} record(s)</strong> including transactions, documents, and other data.</p>
                <p className="text-rose-600 font-medium">Deleting this LLC will permanently destroy all associated data. This cannot be undone.</p>
                <p>Type the LLC name to confirm:</p>
                <Input
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder={llcName}
                  className="font-mono border-rose-300 focus:border-rose-500"
                  data-testid="delete-confirm-name-input"
                  autoFocus
                />
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <Button disabled={!nameMatch} variant="destructive" onClick={() => setStep(2)} data-testid="delete-step1-btn">
                Continue
              </Button>
            </AlertDialogFooter>
          </>
        )}
        {step === 2 && (
          <>
            <AlertDialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-rose-600 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-white" />
                </div>
                <AlertDialogTitle className="text-rose-700">Final Confirmation</AlertDialogTitle>
              </div>
              <AlertDialogDescription className="text-left">
                <p className="text-base font-medium text-slate-900">Are you absolutely sure you want to delete <strong>{llcName}</strong> and all {dataCount.toLocaleString()} record(s)?</p>
                <p className="mt-2 text-rose-600 font-semibold">There is no way to recover this data.</p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <Button variant="destructive" onClick={onDelete} data-testid="delete-final-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Permanently Delete
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteLLCDialog;
